import * as React from 'react';
import { useHistory } from 'react-router';
import { Authority } from '../components/AuthorityLabel';
import { Button } from '../components/Button';
import {
  ConfirmDialog,
  GraphQLErrorDialog,
} from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { PageHeader } from '../components/PageHeader';
import { ToastContext } from '../components/Toast';
import * as Const from '../Const';
import {
  useDeleteFrameMutation,
  useGetConfigLazyQuery,
  useUpdateFramesDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import './FrameListPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';
import { LIST_SEARCH_EMPTY_MESSAGE, useListSearch } from '../components/ListSearch';
import { FileNameButton, FilePreviewWindow, isPreviewableFile } from '../components/FilePreviewWindow';
import { downloadFile } from '../utils/DownloadFile';

/** 拡大表示の窓で開いているファイル */
interface PreviewFile {
  fileName: string;
  fileUrl: string;
}

interface DeleteFrameTarget {
  id: number;
  name: string;
}

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 4;

/**
 * 図枠の編集に必要な権限の段階。
 * この一覧の編集・削除は、共通設定（/configs配下）の画面を開けるマスター編集者以上に出しており、
 * サーバーの並び順の保存（updateFramesDisplayOrder）もマスター編集者以上なので、「並び替え」も同じ段階にする
 */
const EDIT_AUTHORITY_LEVEL = Authority.MasterAdmin;

/**
 * 図枠の一覧
 */
export const FrameListPage: React.FC = function () {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [deleteTarget, setDeleteTarget] = React.useState<DeleteFrameTarget>();
  const [previewFile, setPreviewFile] = React.useState<PreviewFile>();
  const [getConfig, getConfigResult] = useGetConfigLazyQuery();
  const [deleteFrame, deleteFrameResult] = useDeleteFrameMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getConfig();
    },
    onError() {
      // 削除に失敗しても一覧は残し、続けて操作できるようにする
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  // 見出しの右の絞り込みの欄。絞り込んでいる間は並びを変えさせない
  const search = useListSearch();
  const [updateDisplayOrder] = useUpdateFramesDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getConfigResult.data?.getConfig.frames ?? []).map((frame) => frame.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: search.filtering,
    save: (ids) => updateDisplayOrder({ variables: { frameIds: ids } }),
  });

  // ファイル名を押すと、画像は拡大表示の窓を開き、DXFとPDFはそのままダウンロードする
  const openFile = function (fileName: string, fileUrl: string) {
    if (isPreviewableFile(fileName)) {
      setPreviewFile({ fileName, fileUrl });
      return;
    }
    downloadFile(fileUrl, fileName);
  };

  if (!getConfigResult.called) {
    getConfig();
  }

  if (getConfigResult.error) {
    return <GraphQLErrorDialog error={getConfigResult.error} />;
  }

  if (
    getConfigResult.loading ||
    !getConfigResult.data ||
    deleteFrameResult.loading
  ) {
    return <Loading />;
  }

  const frames = getConfigResult.data.getConfig.frames;
  const displayedFrames = orderByIds(frames, reorderMode.displayedIds).filter((frame) =>
    search.matches([frame.name, frame.dxf_file_name])
  );

  let rows;
  if (displayedFrames.length === 0) {
    // 1件も登録されていないときと、絞り込みで1件も残らなかったときで、出す文を分ける
    let emptyMessage = LIST_SEARCH_EMPTY_MESSAGE;
    if (frames.length === 0) {
      emptyMessage = '図枠が登録されていません';
    }
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message={emptyMessage} />;
  } else {
    rows = displayedFrames.map((frame, index) => {
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(frame.id, index)} />;
      }
      const dxfFileContent = (
        <FileNameButton
          fileName={frame.dxf_file_name}
          onClick={() => openFile(frame.dxf_file_name, Const.API_URL + frame.dxf_url)}
        />
      );
      const imageContents = frame.dxf_images.map((image) => (
        <FileNameButton
          key={image.file_url}
          fileName={image.file_name}
          onClick={() => openFile(image.file_name, Const.API_URL + image.file_url)}
        />
      ));
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <Button
              mini={true}
              color="white"
              onClick={() => history.push(`/configs/frames/${frame.id}/edit`)}
            >
              編集
            </Button>
            <Button mini={true} color="red" onClick={() => setDeleteTarget(frame)}>
              削除
            </Button>
          </div>
        </td>
      );

      return (
        <tr key={frame.id} {...reorderMode.getRowProps(frame.id)}>
          {handleCell}
          <td>{frame.name}</td>
          <td>{dxfFileContent}</td>
          <td>
            <div className="FrameListPage-images">{imageContents}</div>
          </td>
          {actionCell}
        </tr>
      );
    });
  }

  let deleteDialog;
  if (deleteTarget) {
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="図枠の削除"
        message={<p>{deleteTarget.name}を削除しますか？</p>}
        yesLabel="削除"
        onYes={() => {
          deleteFrame({ variables: { frameId: deleteTarget.id } });
          setDeleteTarget(undefined);
        }}
        onNo={() => setDeleteTarget(undefined)}
      />
    );
  }

  let previewWindow;
  if (previewFile) {
    previewWindow = (
      <FilePreviewWindow
        fileName={previewFile.fileName}
        src={previewFile.fileUrl}
        onClose={() => setPreviewFile(undefined)}
        onDownload={() => downloadFile(previewFile.fileUrl, previewFile.fileName)}
      />
    );
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <Button mini={true} onClick={() => history.push('/configs/frames/new')}>
        新規作成
      </Button>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th className="App-table_actions_column">操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'frames');

  return (
    <div className="FrameListPage">
      {deleteDialog}
      {previewWindow}
      <PageHeader
        title="図枠設定"
        breadcrumbs={[{ label: '共通設定' }, { label: '図枠設定' }]}
        actions={headerActions}
      />
      <ListSummary
        reorder={reorderMode}
        shownCount={displayedFrames.length}
        search={{ search, placeholder: '名前・図枠ファイル名で絞り込み' }}
      />
      {/* 図枠ファイルや関連画像の名前は長くなりうるので折り返しは止めず、表が収まらないときだけ横にスクロールさせる。
          取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table App-table_full">
          <thead>
            <tr>
              {handleHeader}
              <th>名前</th>
              <th>図枠ファイル</th>
              <th>関連画像</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
